import { TASK_TYPE_DISCIPLINE_MAP } from '../constants/visitTypes';
import type { DisciplineType } from '../services/api/models/DisciplineType';
import type { VisitTypeId } from '../services/api/types.gen';
import { formatDateToISO } from '../utils/dateUtils';
import { EPISODE_START_DEFINING_VISIT_TYPES } from '../utils/episodeBoundaryVisits';

/** One staged visit type paired with the dates it should be created on. */
export interface StagedVisitGroup {
  visitType: VisitTypeId;
  displayName: string;
  /** Unique, ascending. */
  dates: Date[];
}

/** One visit in the bulk-create payload. */
export interface BulkVisitItem {
  visit_type: VisitTypeId;
  discipline: DisciplineType;
  visit_date: string;
  assigned_clinician_id: string;
}

export const dedupeSortedDates = (dates: Date[]): Date[] => {
  const byIso = new Map<string, Date>();
  for (const date of dates) byIso.set(formatDateToISO(date), date);
  return [...byIso.values()].sort((a, b) => a.getTime() - b.getTime());
};

/**
 * Set the exact dates for a visit type in the staged list. A type appears at
 * most once: an empty date list removes it, otherwise its dates are replaced
 * (an existing type keeps its position; a new type is appended).
 */
export const setDatesForType = (
  groups: StagedVisitGroup[],
  visitType: VisitTypeId,
  displayName: string,
  dates: Date[]
): StagedVisitGroup[] => {
  const sorted = dedupeSortedDates(dates);
  if (sorted.length === 0) {
    return groups.filter(group => group.visitType !== visitType);
  }
  if (groups.some(group => group.visitType === visitType)) {
    return groups.map(group =>
      group.visitType === visitType
        ? { ...group, displayName, dates: sorted }
        : group
    );
  }
  return [...groups, { visitType, displayName, dates: sorted }];
};

export const countVisits = (groups: StagedVisitGroup[]): number =>
  groups.reduce((total, group) => total + group.dates.length, 0);

/** How many staged dates are OASIS Start of Care (any discipline). */
export const countOasisSocVisits = (groups: StagedVisitGroup[]): number =>
  groups.reduce(
    (total, group) =>
      total +
      (EPISODE_START_DEFINING_VISIT_TYPES.has(group.visitType)
        ? group.dates.length
        : 0),
    0
  );

/** Flatten staged groups into the bulk endpoint's `visits` payload. Every item
 * is assigned to `assignedClinicianId` (the dialog currently uses one assignee
 * for the whole batch; the payload carries it per item so different assignees
 * per visit can be supported later without an API change). */
export const buildBulkVisitItems = (
  groups: StagedVisitGroup[],
  assignedClinicianId: string
): BulkVisitItem[] =>
  groups.flatMap(group =>
    group.dates.map(date => ({
      visit_type: group.visitType,
      discipline: TASK_TYPE_DISCIPLINE_MAP[group.visitType],
      visit_date: formatDateToISO(date),
      assigned_clinician_id: assignedClinicianId,
    }))
  );
